import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { type CamelCasedStudentNetworkEvent } from 'StudentNetworkEvent';
import { formatEventDate, getEventTypeName, isOnlineEvent } from '../../utils/eventHelpers';

type EventCardProps = {
    event: CamelCasedStudentNetworkEvent;
    onClick?: () => void;
    onCategoryFilter?: (categoryKey: string) => void;
};

// Sketch: EventCard — 303px wide, image 172px 8px corners, title 20px semibold black,
// location 16px medium slate-grey, date 16px medium slate-grey, tags #ecf0f3 12px medium
export function EventCard({ event, onClick, onCategoryFilter }: EventCardProps) {
    const imageUrl = event.image?.formats?.original?.url;
    const locationName = event.anonymized
        ? undefined
        : ((event as Record<string, unknown>).locationName as string | undefined);
    const online = isOnlineEvent(event.eventType);

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={e => e.key === 'Enter' && onClick?.()}
            className="group cursor-pointer"
        >
            {/* Image — Sketch: mobile 360x204, desktop 304x172, 8px corners */}
            <div className="mb-3 aspect-[30/17] w-full overflow-hidden rounded-[8px] border border-transparent bg-slate-grey-50 transition-colors duration-300 group-hover:border-slate-grey">
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt=""
                        className="h-full w-full object-cover opacity-0 transition-opacity duration-300"
                        onLoad={e => {
                            (e.target as HTMLImageElement).classList.remove('opacity-0');
                        }}
                    />
                )}
            </div>

            {/* Title, location, date, tags — stacked with consistent gap */}
            <div className="flex flex-col gap-2">
                <div className="text-[20px] font-semibold leading-tight text-black">{event.title}</div>

                {locationName && !online && <div className="text-base font-medium text-slate-grey">{locationName}</div>}

                <div className="flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faCalendar} className="text-base text-slate-grey" />
                    <span className="text-base font-medium text-slate-grey">{formatEventDate(event)}</span>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={e => {
                            e.stopPropagation();
                            onCategoryFilter?.(event.eventType);
                        }}
                        className="cursor-pointer rounded-full border border-transparent bg-slate-grey-100 px-2.5 py-1.5 text-xxs font-medium text-[#282828] hover:border-slate-grey"
                    >
                        {getEventTypeName(event.eventType)}
                    </button>
                    <button
                        type="button"
                        onClick={e => {
                            e.stopPropagation();
                            onCategoryFilter?.(online ? 'online' : 'in_person');
                        }}
                        className="cursor-pointer rounded-full border border-transparent bg-slate-grey-100 px-2.5 py-1.5 text-xxs font-medium text-[#282828] hover:border-slate-grey"
                    >
                        {online ? 'Online' : 'In-Person'}
                    </button>
                </div>
            </div>
        </div>
    );
}
